import { Button, FloatingIndicator, Tabs } from '@mantine/core';
import { IconArrowLeft, IconBell, IconUser } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PageHeader } from 'src/Components/Navigation/PageHeader';
import { ChangePasswordSection } from 'src/Containers/Profile/ChangePasswordSection';
import { NotificationPreferences } from 'src/Containers/Profile/NotificationPreferences';
import { ProfileDetails } from 'src/Containers/Profile/ProfileDetails';
import { useCheckEmailStatus } from 'src/Hooks/Mutations/useCheckEmailStatus';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import { EmailStatusDtoLoginMethodEnum } from 'src/Services/API/generated';

import styles from './ProfileMainPage.module.scss';

const ProfileMainPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: user } = useFetchUser();
  const [hasEmailPasswordLogin, setHasEmailPasswordLogin] = useState(false);
  const { mutate: checkEmailStatus } = useCheckEmailStatus({
    onSuccess: data => {
      setHasEmailPasswordLogin(data.loginMethod === EmailStatusDtoLoginMethodEnum.EmailPassword);
    },
  });

  useEffect(() => {
    if (user?.email) {
      checkEmailStatus({ email: user.email });
    }
  }, [user?.email]);

  const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null);
  // `?tab=notifications` is the « Choisir les e-mails que je reçois » link in every
  // notification e-mail: it must land on the preferences, not on the profile form.
  const [searchParams] = useSearchParams();
  const [value, setValue] = useState<string | null>(() =>
    searchParams.get('tab') === 'notifications' ? 'notifications' : 'profile'
  );
  const [controlsRefs, setControlsRefs] = useState<Record<string, HTMLButtonElement | null>>({});
  const setControlRef = (val: string) => (node: HTMLButtonElement) => {
    controlsRefs[val] = node;
    setControlsRefs(controlsRefs);
  };

  return (
    <div className={styles.background}>
      <div className={styles.content}>
        <div className={styles.container}>
          <PageHeader
            label={t('profilePage.title')}
            rightElement={
              <Button leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/asset')}>
                {t('common.backToMyAssets', 'Retour à mes biens')}
              </Button>
            }
          />
          <div style={{ marginTop: '3rem' }} />
          <Tabs value={value} onChange={setValue} className={styles.tabs} variant="unstyled">
            <Tabs.List ref={setRootRef} className={styles.tabList}>
              <Tabs.Tab
                value="profile"
                ref={setControlRef('profile')}
                leftSection={<IconUser size={16} />}
              >
                {t('profilePage.tabs.profile', 'Profil')}
              </Tabs.Tab>
              <Tabs.Tab
                value="notifications"
                ref={setControlRef('notifications')}
                leftSection={<IconBell size={16} />}
              >
                {t('profilePage.tabs.notifications', 'Notifications')}
              </Tabs.Tab>

              <FloatingIndicator
                target={value ? controlsRefs[value] : null}
                parent={rootRef}
                className={styles.indicator}
              />
            </Tabs.List>

            <div style={{ marginTop: '2rem' }}>
              <Tabs.Panel value="profile">
                <ProfileDetails />
                {hasEmailPasswordLogin && <ChangePasswordSection />}
              </Tabs.Panel>

              <Tabs.Panel value="notifications">
                <NotificationPreferences />
              </Tabs.Panel>
            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default ProfileMainPage;
